import { ref, watch, type Ref } from 'vue'
import { kycAnswersCountText } from '@staff/shared'

import { fetchKycQuestionStatus } from '@/services/kyc'

/**
 * How many questions each KYC in `kycIds` has answered, as "2 av 10 besvarade", read once per KYC as it
 * appears, and again on `refresh` (after the distributor has saved answers). A KYC whose read failed has
 * no entry, so its count is left out rather than shown wrong.
 */
export function useKycAnswersCount(kycIds: () => string[]): {
  counts: Ref<Record<string, string>>
  refresh: (kycId: string) => Promise<void>
} {
  const counts = ref<Record<string, string>>({})
  const requested = new Set<string>()

  const load = async (kycId: string) => {
    requested.add(kycId)
    try {
      const status = await fetchKycQuestionStatus(kycId)
      const answered = status.questions.filter((question) => question.answered).length
      counts.value = { ...counts.value, [kycId]: kycAnswersCountText(answered, status.questions.length) }
    } catch {
      // The count is a hint next to "Fyll i KYC", which still opens the form; without it the card stays usable.
    }
  }

  watch(
    kycIds,
    (ids) => {
      ids.filter((id) => !requested.has(id)).forEach((id) => void load(id))
    },
    { immediate: true },
  )

  return { counts, refresh: load }
}
